/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { useNodesState, useEdgesState, addEdge } from "reactflow";
import useEditData from "@/hooks/editHooks/useEditData";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { PtwSettingsData } from "./workFlow-context";
import { useTheme } from "@mui/material/styles";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { Chip, Typography, Avatar, Box, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RootIcon from "@mui/icons-material/Home";
import { useTranslation } from "react-i18next";

const StyledNode = styled("div")(({ theme, isRoot, isDarkMode }) => ({
  width: "100%",
  height: "100%",
  backgroundColor: isRoot
    ? theme.palette.primary.main
    : isDarkMode
      ? theme.palette.grey[900]
      : theme.palette.grey[100],
  color: theme.palette.getContrastText(
    isRoot
      ? theme.palette.primary.main
      : isDarkMode
        ? theme.palette.grey[900]
        : theme.palette.grey[100]
  ),
  borderRadius: "16px",
  border: `2px solid ${
    isRoot
      ? theme.palette.primary.dark
      : isDarkMode
        ? theme.palette.grey[700]
        : theme.palette.grey[300]
  }`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: theme.shadows[4],
  position: "relative",
  padding: theme.spacing(2),
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[8],
  },
}));

const useEditWorkflowData = () => {
  const { t } = useTranslation();
  const { selectedWorkflow, userGroupsList, inputTypeList, refetchWorkflows } =
    PtwSettingsData();
  const theme = useTheme();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const { getItem, setItem } = useLocalStorage("workflowdetails");

  const [isDarkMode, setIsDarkMode] = useState(theme.palette.mode === "dark");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [userGroupIds, setUserGroupIds] = useState([]);
  const [modalButton, setModalButton] = useState("");
  const [nodeData, setNodeData] = useState({
    action_name: "",
    status: "",
    self_actionable: false,
    userGroupIds: [],
    step_inputs: [],
  });
  const [workflowData, setWorkflowData] = useState(getItem("workflowdetails"));
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Initialize mutation for editing workflow data
  const editMutation = useEditData(
    `/api/v1/org-admin/workflows/${workflowData?.id}/steps/${selectedNode}`,
    ["workflows", workflowData?.id]
  );

  useEffect(() => {
    setIsDarkMode(theme.palette.mode === "dark");
  }, [theme.palette.mode]);

  // Sync workflowData with selectedWorkflow only if workflowData is empty
  useEffect(() => {
    if (selectedWorkflow && !workflowData) {
      setWorkflowData(selectedWorkflow);
      setItem(selectedWorkflow);
    }
  }, [selectedWorkflow, setItem, workflowData]);

  // Handle opening and closing the modal
  const openUpdateModal = useCallback(
    (nodeId) => {
      const currentWorkflowData =
        getItem("workflowdetails") || selectedWorkflow;
      setModalButton("update");

      const selectedNode = currentWorkflowData.steps.find(
        (step) => step.id === nodeId
      );
      if (selectedNode) {
        setSelectedNode(nodeId);
        setNodeData({
          action_name: selectedNode.action_name,
          status: selectedNode.status,
          self_actionable: selectedNode.self_actionable,
          userGroupIds: selectedNode.user_groups
            ? selectedNode.user_groups.map((group) => group.id)
            : [],
          step_inputs: Array.isArray(selectedNode.step_inputs)
            ? selectedNode.step_inputs.map((input) => ({
                ...input,
                type:
                  inputTypeList.find(
                    (option) => option.value === input.input_type_id
                  )?.input_key || "",
                options: input.options || [],
              }))
            : [],
        });
        setUserGroupIds(
          selectedNode.user_groups
            ? selectedNode.user_groups.map((group) => ({
                value: group.id,
                label:
                  userGroupsList.find((element) => element.value === group.id)
                    ?.label ||
                  group.name ||
                  t("editWorkflowData.unknownLabel"),
              }))
            : []
        );
        setIsModalOpen(true);
      }
    },
    [getItem, selectedWorkflow, userGroupsList, inputTypeList, t]
  );

  const moveStepInputUp = (index) => {
    if (index === 0) return;
    setNodeData((prevNodeData) => {
      const steps = [...prevNodeData.step_inputs];
      [steps[index - 1], steps[index]] = [steps[index], steps[index - 1]];

      const updatedSteps = steps.map((step, idx) => ({
        ...step,
        position: idx,
      }));

      return { ...prevNodeData, step_inputs: updatedSteps };
    });
  };

  const moveStepInputDown = (index) => {
    if (index === nodeData.step_inputs.length - 1) return;
    setNodeData((prevNodeData) => {
      const steps = [...prevNodeData.step_inputs];
      [steps[index], steps[index + 1]] = [steps[index + 1], steps[index]];

      const updatedSteps = steps.map((step, idx) => ({
        ...step,
        position: idx,
      }));

      return { ...prevNodeData, step_inputs: updatedSteps };
    });
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedNode(null);
    setNodeData({
      action_name: "",
      status: "",
      self_actionable: false,
      userGroupIds: [],
      step_inputs: [],
    });
    setUserGroupIds([]);
  }, []);

  // Function to handle multi-select changes for user groups
  const handleMultiChange = useCallback(
    (field) => (selectedOptions) => {
      if (field === "user_groups") {
        setUserGroupIds(selectedOptions);
      }
    },
    []
  );

  // Function to update node data locally
  const updateNodeData = useCallback(() => {
    if (!selectedNode) return;
    const updatedWorkflowData = { ...workflowData };
    const stepIndex = workflowData.steps.findIndex(
      (step) => step.id === selectedNode
    );
    if (stepIndex !== -1) {
      updatedWorkflowData.steps[stepIndex] = {
        ...updatedWorkflowData.steps[stepIndex],
        action_name: nodeData.action_name,
        status: nodeData.status,
        self_actionable: nodeData.self_actionable,
        user_groups: userGroupIds.map((group) => ({
          id: group.value,
          name: group.label,
        })),
        userGroupIds: userGroupIds.map((group) => group.value),
        step_inputs: nodeData.step_inputs.map((input) => ({
          ...input,
          type:
            inputTypeList.find((option) => option.value === input.input_type_id)
              ?.input_key || "",
        })),
      };
    }
    setWorkflowData(updatedWorkflowData);
    setItem(updatedWorkflowData);
    closeModal();
  }, [
    selectedNode,
    nodeData,
    userGroupIds,
    workflowData,
    setItem,
    closeModal,
    inputTypeList,
  ]);

  // Connection handling for ReactFlow
  const onConnect = useCallback(
    (connection) =>
      setEdges((prevEdges) =>
        addEdge({ ...connection, animated: true }, prevEdges)
      ),
    [setEdges]
  );

  // Handling workflow name changes
  const handleChangeName = useCallback(
    (value) => {
      const newName = value;
      setWorkflowData((prevData) => ({
        ...prevData,
        name: newName,
      }));
      setItem({ ...workflowData, name: newName });
    },
    [workflowData, setItem]
  );

  // Reconstruct nodes and edges
  useEffect(() => {
    if (!workflowData) {
      return;
    }

    const nodes = [];
    const edges = [];
    const parentChildMap = new Map();

    nodes.push({
      id: workflowData.id,
      position: { x: 180, y: 0 },
      style: {
        background: "transparent",
        border: "none",
        width: 240,
        height: 120,
        zIndex: 1,
      },
      data: {
        label: (
          <StyledNode isRoot isDarkMode={isDarkMode}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.dark,
                  width: 28,
                  height: 28,
                }}
              >
                <RootIcon fontSize="small" />
              </Avatar>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                textAlign="center"
              >
                {workflowData.name || t("editWorkflowData.rootNodeLabel")}
              </Typography>
            </Stack>
          </StyledNode>
        ),
      },
    });

    workflowData.steps.forEach((step) => {
      const parentId = step.parent_id || workflowData.id;
      if (!parentChildMap.has(parentId)) {
        parentChildMap.set(parentId, []);
      }
      parentChildMap.get(parentId).push(step);
    });

    let currentY = 100;
    const processLevel = (parentId, level = 0) => {
      if (!parentChildMap.has(parentId)) return;
      let currentX = 0;

      parentChildMap.get(parentId).forEach((step) => {
        const nodeId = step.id;
        const x = currentX;
        const y = currentY + level * 180;

        nodes.push({
          id: nodeId,
          position: { x, y },
          style: {
            background: "transparent",
            border: "none",
            width: 240,
            height: 120,
            zIndex: 1,
          },
          data: {
            label: (
              <StyledNode isDarkMode={isDarkMode}>
                <Box
                  onClick={() => openUpdateModal(nodeId)}
                  sx={{
                    height: "100%",
                    width: "100%",
                    px: 1.5,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? theme.palette.grey[900]
                        : theme.palette.grey[100],
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    align="center"
                  >
                    {t("editWorkflowData.statusLabel", {
                      status:
                        step.status || t("editWorkflowData.unknownStatusLabel"),
                    })}
                  </Typography>
                  {step.self_actionable && (
                    <Chip
                      label={t("editWorkflowData.selfActionableLabel")}
                      color="success"
                      size="small"
                      icon={<CheckCircleIcon />}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              </StyledNode>
            ),
          },
        });
        currentX += 240;
        processLevel(nodeId, level + 1);
      });
    };

    processLevel(workflowData.id);

    parentChildMap.forEach((childSteps, parentId) => {
      childSteps.forEach((step) => {
        const source = parentId;
        const target = step.id;
        edges.push({
          id: `edge-${source}-${target}`,
          source,
          target,
          label: step.action_name || t("editWorkflowData.unknownLabel"),
        });
      });
    });

    setNodes(nodes);
    setEdges(edges);
  }, [workflowData, isDarkMode, setNodes, setEdges, t]);

  // Helper function to process default_value based on input type
  const processDefaultValue = useCallback((defaultValue, inputTypeKey) => {
    // If no default value provided, return null
    if (!defaultValue || defaultValue === "") {
      return null;
    }

    const key = inputTypeKey?.toLowerCase();

    switch (key) {
      case "short_text":
      case "text":
        // Text types: keep as string
        return defaultValue;

      case "numeric": {
        // Numeric: convert to number, return null if not a valid number
        const numValue = Number(defaultValue);
        return isNaN(numValue) ? null : numValue;
      }

      case "boolean": {
        // Boolean: convert to actual boolean
        if (typeof defaultValue === "boolean") {
          return defaultValue;
        }
        const strValue = String(defaultValue).toLowerCase();
        return strValue === "true" || strValue === "yes" || strValue === "1";
      }

      case "confirmation":
      case "file_upload":
      case "timestamp":
      case "selection":
      case "multi_selection":
      case "user_selection":
      case "multi_user_selection":
        // These types: always null (no default values)
        return null;

      default:
        return null;
    }
  }, []);

  // Handle updating node data and sending to backend
  const handleUpdateNodeData = useCallback(
    async (e) => {
      e.preventDefault();
      if (!selectedNode || !workflowData) return;

      const updatedNodeData = {
        action_name: nodeData.action_name,
        status: nodeData.status,
        self_actionable: nodeData.self_actionable,
        user_group_ids: userGroupIds.map((group) => group.value),
        step_inputs: nodeData.step_inputs.map((input) => {
          const inputType = inputTypeList.find(
            (option) => option.value === input.input_type_id
          );
          const processedDefaultValue = processDefaultValue(
            input.default_value,
            inputType?.input_key
          );

          return {
            input_type_id: input.input_type_id,
            type: inputType?.input_key || "",
            name: input.name,
            description: input.description || "",
            position: input.position,
            required: input.required || false,
            default_value: processedDefaultValue,
            ...(input.options && input.options.length > 0
              ? { options: input.options }
              : {}),
          };
        }),
      };

      try {
        await editMutation.mutateAsync(updatedNodeData);
        updateNodeData();
        setToast({
          open: true,
          severity: "success",
          message: t("editWorkflowData.stepUpdatedToast"),
        });
        refetchWorkflows();
      } catch (err) {
        handleApiError(err);
      }
    },
    [
      selectedNode,
      nodeData,
      userGroupIds,
      workflowData,
      editMutation,
      updateNodeData,
      refetchWorkflows,
      setToast,
      handleApiError,
      inputTypeList,
      processDefaultValue,
      t,
    ]
  );

  // Handle step input changes
  const handleInputTypeChange = useCallback(
    (index, field, value) => {
      setNodeData((prev) => {
        const newStepInputs = [...(prev.step_inputs || [])];

        if (!newStepInputs[index]) {
          newStepInputs[index] = {
            input_type_id: "",
            type: "",
            name: "",
            description: "",
            required: false,
            default_value: "",
            position: 0,
            options: [],
          };
        }

        if (field === "input_type_id") {
          const newValue = typeof value === "object" ? value.value : value;
          const selectedInputType = inputTypeList.find(
            (option) => option.value === newValue
          );
          newStepInputs[index] = {
            ...newStepInputs[index],
            input_type_id: newValue,
            type: selectedInputType ? selectedInputType.input_key : "",
            options:
              selectedInputType?.input_key === "selection" ||
              selectedInputType?.input_key === "multi_selection"
                ? newStepInputs[index].options || []
                : [],
          };
        } else if (field === "position") {
          const newValue = parseInt(value, 10);
          newStepInputs[index] = {
            ...newStepInputs[index],
            position: isNaN(newValue) || newValue < 0 ? 0 : newValue,
          };
        } else {
          newStepInputs[index] = {
            ...newStepInputs[index],
            [field]: value,
          };
        }

        return { ...prev, step_inputs: newStepInputs };
      });
    },
    [inputTypeList]
  );

  const addNewStepInput = useCallback(() => {
    setNodeData((prev) => {
      const existingSteps = Array.isArray(prev.step_inputs)
        ? prev.step_inputs
        : [];
      const nextPosition =
        existingSteps.length > 0
          ? Math.max(...existingSteps.map((step) => step.position)) + 1
          : 0;

      return {
        ...prev,
        step_inputs: [
          ...existingSteps,
          {
            input_type_id: "",
            type: "",
            name: "",
            description: "",
            required: false,
            default_value: "",
            position: nextPosition,
            options: [],
          },
        ],
      };
    });
  }, []);

  const removeStepInput = useCallback((index) => {
    setNodeData((prev) => {
      const newStepInputs = [...(prev.step_inputs || [])];
      newStepInputs.splice(index, 1);
      return { ...prev, step_inputs: newStepInputs };
    });
  }, []);

  const addOption = useCallback((index, option) => {
    if (!option.trim()) return;
    setNodeData((prev) => {
      const newStepInputs = [...(prev.step_inputs || [])];
      newStepInputs[index] = {
        ...newStepInputs[index],
        options: [...(newStepInputs[index].options || []), option.trim()],
      };
      return { ...prev, step_inputs: newStepInputs };
    });
  }, []);

  const removeOption = useCallback((index, optionIndex) => {
    setNodeData((prev) => {
      const newStepInputs = [...(prev.step_inputs || [])];
      newStepInputs[index] = {
        ...newStepInputs[index],
        options: newStepInputs[index].options.filter(
          (_, i) => i !== optionIndex
        ),
      };
      return { ...prev, step_inputs: newStepInputs };
    });
  }, []);

  return {
    nodeData,
    setNodeData,
    nodes,
    onNodesChange,
    edges,
    onEdgesChange,
    isModalOpen,
    userGroupIds,
    modalButton,
    workflowData,
    userGroupsList,
    inputTypeList,
    closeModal,
    updateNodeData,
    onConnect,
    handleChangeName,
    handleUpdateNodeData,
    handleMultiChange,
    editIsLoading: editMutation.isLoading,
    toast,
    handleToastClose,
    handleInputTypeChange,
    addNewStepInput,
    removeStepInput,
    moveStepInputDown,
    moveStepInputUp,
    addOption,
    removeOption,
  };
};

export default useEditWorkflowData;
